import { isEmpty, isObject, isString, flatten, toPairs, isNil, isFunction, map } from 'lodash';

import errorHandler from '@core/lib/errorHandler/errorHandler';

/**
 * @param {Object|String} error
 * @returns {Array[String]}
 */
export const getError = error => {
  if (isEmpty(error)) {
    throw new Error(`Expected "error" param not to be empty.`);
  }

  if (!isObject(error) && !isString(error)) {
    throw new Error(`Expected value for error '${error}' to be a type string or object, but got ${typeof error}`);
  }

  return isObject(error) ? flatten(toPairs(error)) : [error, ''];
};

/**
 * @param {Object} messages
 * @param {String} key
 * @param {String} value
 * @returns {String|Function}
 */
export const getErrorMessage = (messages, key, value) => {
  if (!isObject(messages)) {
    throw new Error(`Expected value for param "messages" to be object, but got ${typeof messages}`);
  }

  const errorMessage = messages[key];

  if (isNil(errorMessage)) {
    throw new Error(`Undefined error message for key '${key}'`);
  }

  if (!isFunction(errorMessage) && !isString(errorMessage)) {
    throw new Error(`Expected value for key '${key}' to be a type string or function, but got ${typeof errorMessage}`);
  }

  if (isFunction(errorMessage) && isNil(value)) {
    throw new Error(`Expected value for key '${key}' to not be empty`);
  }

  return errorMessage;
};

/**
 * @param {Array} data
 * @param {Object} messages
 * @returns {Array[*]}
 */
export const getErrors = (data, messages) => {
  if (isEmpty(data)) {
    return [t('error.an-error-occurred.try-again-later')];
  }

  return map(data, item => {
    const [key, value] = getError(item);

    try {
      const errorMessage = getErrorMessage(messages, key, value);

      if (isFunction(errorMessage)) {
        return errorMessage(value);
      }

      return errorMessage;
    } catch (e) {
      errorHandler.trackError(e.message);
      return t('error.an-error-occurred.try-again-later');
    }
  });
};

/**
 * @param {Array} data
 * @param {Object} messages
 * @returns {*}
 */
export const getFirstError = (data, messages) => getErrors(data, messages)[0];

/**
 * Get error messages from server response or fallback to default error message
 *
 * @param {Object=} data error data object
 * @returns {string} error message
 */
export function getErrorMessageFromResponse(data) {
  let error = data?.errors || [];

  if (isString(error)) {
    error = [error];
  } else if (isObject(error) && !Array.isArray(error)) {
    error = map(error, function (values) {
      return values.join(', ');
    });
  }

  return Object.values(error).join(', ') || t('error.an-error-occurred.try-again-later');
}
